import { RefObject, useLayoutEffect, useRef } from 'react';
import { useDatasetSessionStorageContext } from '../../../hooks/context/useDatasetSessionStorageContext';
import { scrollMenuItemIntoView } from '../utils/scrollMenuItemIntoView';
import { applyMenuScrollUntilSettled } from '../utils/applyMenuScrollUntilSettled';

type UseMenuScrollProps = {
  // The menu's scroll container (.sidebar-menu).
  scrollRef: RefObject<HTMLElement | null>;
  // True once useActiveMenu knows the active item for this route.
  ready: boolean;
  // data-menu-url of the active item — the item we center on.
  selectedItem: string | null;
  // Current search text; a dep so we re-center on each keystroke too.
  searchQuery: string;
};

// The Sidebar is a single persistent instance (it never remounts on navigation), so its scroll position
// is naturally kept — we only need to MOVE it when the active item might be off-screen: on reload, while
// filtering, right after clearing the search, or when navigating from a search result. Plain navigation
// (clicking a menu item, opening a detail/form) does nothing and the menu stays where it was.
export const useMenuScroll = ({
  scrollRef,
  ready,
  selectedItem,
  searchQuery,
}: UseMenuScrollProps) => {
  const { getDatasetSessionStorageState, setDatasetSessionStorageState } =
    useDatasetSessionStorageContext();
  const storageRef = useRef({
    getDatasetSessionStorageState,
    setDatasetSessionStorageState,
  });
  storageRef.current = {
    getDatasetSessionStorageState,
    setDatasetSessionStorageState,
  };

  // Previous values, to detect the search being cleared and a real item change.
  const prevSearchQueryRef = useRef(searchQuery);
  const prevSelectedItemRef = useRef<string | null>(null);
  // True until this Sidebar instance's first scroll decision. The Sidebar is persistent across
  // navigation, so a fresh mount only happens on a document reload or a login — exactly when we
  // want to center the active item. (Per-instance, not module-scoped, so logout→login re-centers too.)
  const isFirstRunRef = useRef(true);

  // useLayoutEffect so the scroll is applied before the browser paints.
  useLayoutEffect(() => {
    if (!ready) return;
    const container = scrollRef.current;
    if (!container || !selectedItem) return;

    const isSearching = searchQuery.trim().length > 0;
    const wasSearching = prevSearchQueryRef.current.trim().length > 0;
    prevSearchQueryRef.current = searchQuery;
    const justClearedSearch = wasSearching && !isSearching;

    const initialMount = isFirstRunRef.current;
    isFirstRunRef.current = false;
    const itemChanged = prevSelectedItemRef.current !== selectedItem;
    prevSelectedItemRef.current = selectedItem;

    // One-shot intent set in Sidebar when navigating from a search result; consumed once the active item
    // actually changes to the result (so the search-clear render on the old item can't swallow it).
    const { menuScrollCenterOnce } =
      storageRef.current.getDatasetSessionStorageState();
    const centerForResult = itemChanged && !!menuScrollCenterOnce;
    if (centerForResult) {
      storageRef.current.setDatasetSessionStorageState({
        menuScrollCenterOnce: undefined,
      });
    }

    const shouldCenter =
      initialMount ||
      isSearching ||
      (justClearedSearch && !menuScrollCenterOnce) ||
      centerForResult;

    // Plain navigation: leave the menu where it is (the persistent DOM keeps its scroll).
    if (!shouldCenter) return;

    const controller = new AbortController();
    applyMenuScrollUntilSettled(
      container,
      () => scrollMenuItemIntoView(container, selectedItem),
      controller.signal
    );

    return () => controller.abort();
  }, [ready, searchQuery, selectedItem, scrollRef]);
};
